import { Card, Skeleton } from '@heroui/react';

// Ενδεικτικό περιεχόμενο κατά τη φόρτωση, αντί για κενή οθόνη ή αναμονή χωρίς
// ένδειξη. Ο χρήστης βλέπει αμέσως τη δομή που θα εμφανιστεί, οπότε η μετάβαση
// στα πραγματικά δεδομένα δεν αναδιατάσσει τη σελίδα (ΜΛΑ-03).
//
// Οι σκελετοί ήταν δικές μας κλάσεις σε `theme.css` με δικό τους `@keyframes`.
// Τώρα στηρίζονται στο `Skeleton` του HeroUI, που φέρνει το shimmer μαζί. Η
// αντίθεση του shimmer διορθώνεται μία φορά στο `index.css`: η προεπιλογή της
// βιβλιοθήκης περνά λάμψη στο ίδιο χρώμα με τη βάση, δηλαδή κινείται χωρίς να
// φαίνεται.
//
// Τα `aria-hidden` και `aria-busy` δεν είναι διακοσμητικά: ο σκελετός είναι
// ψευδοπεριεχόμενο και δεν πρέπει να ανακοινώνεται ως κείμενο.

/** Μια γραμμή ψευδοκειμένου. Το `width` δίνεται ως ποσοστό ή utility. */
export function SkeletonText({ width = '100%', className = '' }) {
    return <Skeleton className={`h-4 rounded ${className}`} style={{ width }} />;
}

/** Κάρτα συλλογής σε αναμονή. Ίδιες διαστάσεις με το `CollectionThumb`. */
export function SkeletonThumb() {
    return (
        <Card className="gap-3">
            <Skeleton className="h-6 w-4/5 rounded" />
            <Skeleton className="h-6 w-24 rounded-full" />
            <Skeleton className="h-4 w-2/5 rounded" />
        </Card>
    );
}

/** Κάρτα πόρου σε αναμονή, με τη θέση της εικόνας. */
export function SkeletonCard() {
    return (
        <Card className="mb-4 gap-3 p-0">
            <Skeleton className="h-40 w-full rounded-t-2xl" />
            <div className="flex flex-col gap-2 p-4 pt-0">
                <Skeleton className="h-6 w-3/5 rounded" />
                <Skeleton className="h-4 w-full rounded" />
                <Skeleton className="h-4 w-4/5 rounded" />
            </div>
        </Card>
    );
}

/** Πλέγμα καρτών συλλογών σε αναμονή. */
export function SkeletonThumbGrid({ count = 4 }) {
    return (
        <div
            className="grid gap-4 sm:grid-cols-2 md:grid-cols-3"
            aria-busy="true"
            aria-hidden="true"
        >
            {Array.from({ length: count }, (unused, index) => <SkeletonThumb key={index} />)}
        </div>
    );
}

/**
 * Στήλες καρτών πόρων σε αναμονή.
 *
 * Το `card-columns` του Bootstrap 4 ήταν `column-count` σε CSS. Το ίδιο
 * αποτέλεσμα με utilities του Tailwind, ώστε οι κάρτες να στοιχίζονται σε στήλες
 * μεταβλητού ύψους αντί για πλέγμα με κενά.
 */
export function SkeletonCardColumns({ count = 6 }) {
    return (
        <div className="columns-1 gap-4 md:columns-2 xl:columns-3" aria-busy="true" aria-hidden="true">
            {Array.from({ length: count }, (unused, index) => <SkeletonCard key={index} />)}
        </div>
    );
}

export default SkeletonText;
